import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "./NewTaskModal.css";
import toast from "react-hot-toast";

const NewTaskModal = ({
  isOpen,
  onClose,
  refreshNotes,
  refreshFilteredNotes,
  userworksaces = [],
  source = "dashboard",
  defaultWorkspaceRole,
  fetchNotesWithFilters,
  selectedFilters,
  hasActiveFilters,
  viewMode = "cards",
  refreshStackedView = null,
  hasActiveSearchText,
  performSearching,
  searchTerm
}) => {
  // State declarations
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedJob, setSelectedJob] = useState("");
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  // Add mode: create one task, or create many tasks for the selected job.
  const [taskAddMode, setTaskAddMode] = useState("single"); // 'single' | 'bulk'
  const [bulkPasteContent, setBulkPasteContent] = useState("");
  const [bulkParsedTasks, setBulkParsedTasks] = useState([]); // [{ title, description }]
  const [isBulkReviewMode, setIsBulkReviewMode] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
  const modalRef = useRef(null);

  // New search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [allSearchData, setAllSearchData] = useState([]);

  // Store the actual project and job objects for dropdown display
  const [selectedProjectData, setSelectedProjectData] = useState(null);
  const [selectedJobData, setSelectedJobData] = useState(null);
  
  // Track if description was manually edited - IMPORTANT for auto-population
  const [isDescriptionManuallyEdited, setIsDescriptionManuallyEdited] = useState(false);

  const searchInputRef = useRef(null);
  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // Check if user has read-only access (role 3)
  const isReadOnly = defaultWorkspaceRole === 3;

  // Auto-copy task name to description when task name changes, but only if description hasn't been manually edited
  useEffect(() => {
    if (!isReadOnly && !isDescriptionManuallyEdited && taskTitle) {
      setTaskDescription(taskTitle);
    } else if (!isReadOnly && !isDescriptionManuallyEdited && !taskTitle) {
      setTaskDescription("");
    }
  }, [taskTitle, isDescriptionManuallyEdited, isReadOnly]);

  // Reset description manual edit flag when task title is cleared
  useEffect(() => {
    if (!taskTitle) {
      setIsDescriptionManuallyEdited(false);
    }
  }, [taskTitle]);

  // Utility functions
  const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user || { id: 1 };
  };

  const normalizeProjectData = (projectData, fallbackData = null) => {
    if (!projectData && !fallbackData) return null;

    const data = projectData || fallbackData;
    return {
      id: data.id || data.projectId,
      projectId: data.projectId || data.id,
      name: data.name || data.projectName || "Unnamed Project",
      workspaceId:
        data.workspaceId || (fallbackData ? fallbackData.workspaceId : null),
      ...data,
    };
  };

  const normalizeJobData = (jobData, fallbackData = null) => {
    if (!jobData && !fallbackData) return null;

    const data = jobData || fallbackData;
    return {
      id: data.id || data.jobId,
      jobId: data.jobId || data.id,
      name: data.name || data.jobName || "Unnamed Job",
      projectId:
        data.projectId || (fallbackData ? fallbackData.projectId : null),
      ...data,
    };
  };

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setShowSearchResults(false);
      setSelectedProjectData(null);
      setSelectedJobData(null);
      setApiError(null);
      setTaskTitle("");
      setTaskDescription("");
      setTaskAddMode("single");
      setBulkPasteContent("");
      setBulkParsedTasks([]);
      setIsBulkReviewMode(false);
      setIsDescriptionManuallyEdited(false);
      setErrors({});
    }
  }, [isOpen]);

  // Fetch all search data when modal opens
  useEffect(() => {
    const fetchAllSearchData = async () => {
      if (!isOpen || isReadOnly) return;

      try {
        const user = getCurrentUser();
        const response = await fetch(
          `${apiUrl}/SiteNote/SearchJobs?userId=${user.id}&search=`,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch search data: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        const allData = data.results || data || [];
        setAllSearchData(allData);
      } catch (error) {
        console.error("Error fetching search data:", error);
        setApiError("Failed to load search data");
      }
    };

    fetchAllSearchData();
  }, [isOpen, apiUrl, isReadOnly]);

  // Fetch projects when workspace changes
  useEffect(() => {
    const fetchProjectsByWorkspace = async () => {
      if (isReadOnly) return;

      if (!selectedWorkspace) {
        setFilteredProjects([]);
        setSelectedProject("");
        setSelectedProjectData(null);
        return;
      }

      setIsLoadingProjects(true);
      try {
        const user = getCurrentUser();
        const response = await fetch(
          `${apiUrl}/Project/GetProjectsByUserJobPermission/${user.id}/${selectedWorkspace}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }

        const data = await response.json();
        const projectsData = data.projects || [];
        setFilteredProjects(projectsData);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setApiError("Failed to load projects");
        setFilteredProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjectsByWorkspace();
  }, [selectedWorkspace, apiUrl, isReadOnly]);

  // Fetch jobs when project changes
  useEffect(() => {
    const fetchJobsByProject = async () => {
      if (isReadOnly) return;

      if (!selectedProject) {
        setFilteredJobs([]);
        setSelectedJob("");
        setSelectedJobData(null);
        setBulkPasteContent("");
        setBulkParsedTasks([]);
        setIsBulkReviewMode(false);
        return;
      }

      setIsLoadingJobs(true);
      setBulkPasteContent("");
      setBulkParsedTasks([]);
      setIsBulkReviewMode(false);
      try {
        const user = getCurrentUser();
        const response = await fetch(
          `${apiUrl}/UserJobAuth/GetJobsByUserAndProject/${user.id}/${selectedProject}`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch jobs");
        }

        const data = await response.json();
        const jobsData = data.jobs || [];
        setFilteredJobs(jobsData);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setApiError("Failed to load jobs");
        setFilteredJobs([]);
      } finally {
        setIsLoadingJobs(false);
      }
    };

    fetchJobsByProject();
  }, [selectedProject, apiUrl, isReadOnly]);

  // Initialize modal state when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedWorkspace("");
      setSelectedProject("");
      setSelectedJob("");
      setTaskTitle("");
      setTaskDescription("");
      setTaskAddMode("single");
      setBulkPasteContent("");
      setBulkParsedTasks([]);
      setIsBulkReviewMode(false);
      setFilteredProjects([]);
      setFilteredJobs([]);
      setSelectedProjectData(null);
      setSelectedJobData(null);
      setIsSaving(false);
      setApiError(null);
      setIsDescriptionManuallyEdited(false);
      setErrors({});
    }
  }, [isOpen]);

  // Client-side search function
  const performSearch = useCallback(
    (query) => {
      if (!query.trim() || isReadOnly) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);

      setTimeout(() => {
        try {
          const lowerCaseQuery = query.toLowerCase().trim();

          const filtered = allSearchData.filter((item) => {
            if (!item) return false;

            const searchText = `
              ${item.workspaceName || ""} 
              ${item.projectName || ""} 
              ${item.jobName || ""}
              ${item.fullPath || ""}
            `.toLowerCase();

            return searchText.includes(lowerCaseQuery);
          });

          setSearchResults(filtered);
          setShowSearchResults(true);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [allSearchData, isReadOnly],
  );

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (searchQuery.trim() && !isReadOnly) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch, isOpen, isReadOnly]);

  // Get project options for dropdown
  const getProjectOptions = () => {
    const options = [...filteredProjects];

    if (
      selectedProjectData &&
      (selectedProjectData.id || selectedProjectData.projectId)
    ) {
      const projectId = (
        selectedProjectData.id || selectedProjectData.projectId
      ).toString();
      const exists = options.some((p) => {
        const pId = (p.id || p.projectId)?.toString();
        return pId === projectId;
      });

      if (!exists) {
        options.push(selectedProjectData);
      }
    }

    return options.filter(
      (project) => project && (project.id || project.projectId),
    );
  };

  // Get job options for dropdown
  const getJobOptions = () => {
    const options = [...filteredJobs];

    if (selectedJobData && (selectedJobData.id || selectedJobData.jobId)) {
      const jobId = (selectedJobData.id || selectedJobData.jobId).toString();
      const exists = options.some((j) => {
        const jId = (j.id || j.jobId)?.toString();
        return jId === jobId;
      });

      if (!exists) {
        options.push(selectedJobData);
      }
    }

    return options.filter((job) => job && (job.id || job.jobId));
  };

  const parseBulkTasks = (content) => {
    const trimmed = (content || "").trim();
    if (!trimmed) return [];

    const lines = trimmed
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    return lines
      .map((line) => {
        const [titleRaw, descriptionRaw] = line.split("\t").map((s) => (s || "").trim());
        const title = titleRaw || "";
        const description = descriptionRaw || "";
        if (!title) return null;
        return { title, description };
      })
      .filter(Boolean);
  };

  const handleBulkReview = () => {
    if (isReadOnly) return;
    const parsed = parseBulkTasks(bulkPasteContent);
    setBulkParsedTasks(parsed);
    setIsBulkReviewMode(true);

    // Clear title validation once we have something to review
    setErrors((prev) => ({ ...prev, title: undefined }));
  };

  const handleBulkTaskEdit = (index, field, value) => {
    setBulkParsedTasks((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setErrors((prev) => ({ ...prev, title: undefined }));
  };

  const handleBulkTaskDelete = (index) => {
    setBulkParsedTasks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkAddRow = () => {
    setBulkParsedTasks((prev) => [...prev, { title: "", description: "" }]);
  };

  const handleBulkClear = () => {
    setBulkPasteContent("");
    setBulkParsedTasks([]);
    setIsBulkReviewMode(false);
    setErrors((prev) => ({ ...prev, title: undefined }));
  };

  // Handle search result selection
  const handleSearchResultSelect = useCallback(
    async (result) => {
      if (!result || isReadOnly) return;

      setSelectedWorkspace("");
      setSelectedProject("");
      setSelectedJob("");
      setFilteredProjects([]);
      setFilteredJobs([]);
      setSelectedProjectData(null);
      setSelectedJobData(null);
      setApiError(null);

      try {
        if (result.workspaceId) {
          const matchingWorkspace = userworksaces.find(
            (workspace) =>
              workspace &&
              workspace.id &&
              workspace.id.toString() === result.workspaceId.toString(),
          );
          if (matchingWorkspace) {
            setSelectedWorkspace(matchingWorkspace.id.toString());
          }
        }

        if (result.projectId) {
          setIsLoadingProjects(true);
          try {
            const projectResponse = await fetch(
              `${apiUrl}/Project/GetProjectById/${result.projectId}`,
            );

            if (projectResponse.ok) {
              const projectData = await projectResponse.json();
              const normalizedProject = normalizeProjectData(
                projectData,
                result,
              );
              if (normalizedProject) {
                setSelectedProjectData(normalizedProject);
                setSelectedProject(normalizedProject.id.toString());
              }
            }
          } catch (projectError) {
            const fallbackProject = normalizeProjectData(null, result);
            if (fallbackProject) {
              setSelectedProjectData(fallbackProject);
              setSelectedProject(fallbackProject.id.toString());
            }
          }
        }

        if (result.jobId) {
          await new Promise((resolve) => setTimeout(resolve, 100));

          setIsLoadingJobs(true);
          try {
            const jobResponse = await fetch(
              `${apiUrl}/Job/GetJobById/${result.jobId}`,
            );

            if (jobResponse.ok) {
              const jobData = await jobResponse.json();
              const normalizedJob = normalizeJobData(jobData, result);
              if (normalizedJob) {
                setSelectedJobData(normalizedJob);
                setSelectedJob(normalizedJob.id.toString());
              }
            }
          } catch (jobError) {
            const fallbackJob = normalizeJobData(null, result);
            if (fallbackJob) {
              setSelectedJobData(fallbackJob);
              setSelectedJob(fallbackJob.id.toString());
            }
          }
        }
      } catch (error) {
        console.error("Error in search result selection:", error);
        setApiError(`Failed to load selection: ${error.message}`);
      } finally {
        setIsLoadingProjects(false);
        setIsLoadingJobs(false);
      }

      setSearchQuery("");
      setShowSearchResults(false);
      setSearchResults([]);
    },
    [userworksaces, apiUrl, isReadOnly],
  );

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchResults]);

  // Save task to JobTasks endpoint
  const handleSave = async () => {
    if (isReadOnly) return;

    const newErrors = {};
    if (!selectedJob) newErrors.job = "Please select a job";
    if (!taskTitle.trim()) newErrors.title = "Task title is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setApiError(null);

    try {
      const user = getCurrentUser();
      
      // Current date in ISO format
      const now = new Date().toISOString();

      // Get description content - use task title if description is empty and not manually edited
      let descriptionContent = taskDescription.trim();
      if (!descriptionContent && !isDescriptionManuallyEdited) {
        descriptionContent = taskTitle.trim();
      }

      const taskData = {
        title: taskTitle.trim(),
        description: descriptionContent || taskTitle.trim(),
        startDate: now,
        endDate: now,
        dueDate: now,
        assigneeId: user.id,
        createdById: user.id,
        jobId: parseInt(selectedJob, 10),
        status: 1 // Active status
      };

      console.log("Saving task with data:", taskData);

      const response = await fetch(`${apiUrl}/JobTasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(taskData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to save task";
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.title || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const savedTask = await response.json();
      
      toast.success("Task saved successfully!");

      // Refresh notes or tasks list
      if (refreshNotes) {
        await refreshNotes();
      }

      onClose();
    } catch (error) {
      console.error("Save error:", error);
      setApiError(error.message || "Failed to save task");
      toast.error("Failed to save task");
    } finally {
      setIsSaving(false);
    }
  };

  // Bulk save tasks to JobTasks endpoint
  const handleSaveBulk = async () => {
    if (isReadOnly) return;

    const newErrors = {};
    if (!selectedJob) {
      newErrors.job = "Please select a job";
    }
    const validTasks = (bulkParsedTasks || [])
      .map((t) => ({
        title: (t?.title || "").trim(),
        description: (t?.description || "").trim(),
      }))
      .filter((t) => t.title);

    if (validTasks.length === 0) {
      newErrors.title = "Provide at least one task title";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setApiError(null);

    try {
      const user = getCurrentUser();
      const now = new Date().toISOString();

      const tasks = validTasks.map((t) => ({
        title: t.title,
        description: t.description || t.title,
        startDate: now,
        endDate: now,
        dueDate: now,
        assigneeId: user.id,
        createdById: user.id,
        jobId: parseInt(selectedJob, 10),
        status: 1, // Active status
      }));

      const response = await fetch(`${apiUrl}/JobTasks/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ tasks }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to save tasks";

        try {
          const errorData = JSON.parse(errorText);
          errorMessage =
            errorData.message || errorData.title || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json().catch(() => ({}));
      toast.success(`${tasks.length} task${tasks.length === 1 ? "" : "s"} saved successfully!`);

      if (refreshNotes) {
        await refreshNotes();
      }

      onClose();
    } catch (error) {
      console.error("Bulk save error:", error);
      setApiError(error.message || "Failed to save tasks");
      toast.error("Failed to save tasks");
    } finally {
      setIsSaving(false);
    }
  };

  // Keyboard shortcut for save
  const handleSaveShortcut = useCallback(
    (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === "s" &&
        !isSaving &&
        !isReadOnly
      ) {
        event.preventDefault();
        if (taskAddMode === "bulk") handleSaveBulk();
        else handleSave();
      }
    },
    [isSaving, handleSave, handleSaveBulk, isReadOnly, taskAddMode],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleSaveShortcut);
      return () =>
        document.removeEventListener("keydown", handleSaveShortcut);
    }
  }, [isOpen, handleSaveShortcut]);

  useEffect(() => {
    if (!isOpen) return;

    const hasErrors = apiError || (errors && Object.keys(errors).length > 0);
    if (hasErrors && modalRef.current) {
      try {
        if (typeof modalRef.current.scrollTo === "function") {
          modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          modalRef.current.scrollTop = 0;
        }
      } catch (e) {
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (_) {}
      }
    }
  }, [apiError, errors, isOpen]);

  // Search component
  const renderSearchSection = () => (
    <div className="search-section">
      <div
        className="form-group full-width search-container"
        ref={searchInputRef}
      >
        <label>🔍 Quick Search</label>
        <div className="search-input-wrapper" style={{ width: "100%" }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type to search for workspace, project, or job..."
            className="search-input"
            style={{ width: "100%" }}
            disabled={isReadOnly}
          />
          {isSearching && !isReadOnly && (
            <div className="search-spinner">🔍 Searching...</div>
          )}
        </div>

        {showSearchResults && searchResults.length > 0 && !isReadOnly && (
          <div className="search-results-dropdown" style={{ width: "100%" }}>
            <div className="search-results-header">
              Found {searchResults.length} result
              {searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
            </div>
            {searchResults.map((result, index) => (
              <div
                key={`${result.workspaceId}-${result.projectId}-${result.jobId}-${index}`}
                className="search-result-item"
                onClick={() => handleSearchResultSelect(result)}
              >
                <div className="search-result-fullpath">
                  {result.fullPath ||
                    `${result.workspaceName} → ${result.projectName} → ${result.jobName}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {showSearchResults &&
          searchQuery &&
          searchResults.length === 0 &&
          !isSearching &&
          !isReadOnly && (
            <div
              className="search-results-dropdown"
              style={{ width: "100%" }}
            >
              <div className="search-no-results">
                No results found for "<strong>{searchQuery}</strong>"
                <div style={{ fontSize: "11px", marginTop: "4px" }}>
                  Try searching with different keywords
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );

  // Main content
  const projectOptions = getProjectOptions();
  const jobOptions = getJobOptions();

  return isOpen ? (
    <div className="edit-note-modal-overlay">
      <div className="edit-note-modal" ref={modalRef}>
        <div className="modal-header">
          <h2>New Task</h2>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        {apiError && (
          <div className="error-message">
            {apiError}
            <button
              onClick={() => setApiError(null)}
              className="dismiss-error"
            >
              ×
            </button>
          </div>
        )}

        {!isReadOnly && renderSearchSection()}

        <div className="form-group">
          <label>Workspace</label>
          <select
            value={selectedWorkspace}
            onChange={(e) => setSelectedWorkspace(e.target.value)}
            disabled={isReadOnly}
          >
            <option value="">Select Workspace</option>
            {userworksaces.map(
              (workspace) =>
                workspace &&
                workspace.id && (
                  <option key={workspace.id} value={workspace.id.toString()}>
                    {workspace.name}
                  </option>
                ),
            )}
          </select>
        </div>

        <div className="form-group">
          <label>
            Project{" "}
            {errors.project && (
              <span className="error-message-inline">{errors.project}</span>
            )}
          </label>
          <select
            value={selectedProject}
            onChange={(e) => {
              setSelectedProject(e.target.value);
              const selectedProjectObj = projectOptions.find((p) => {
                const pId = (p.id || p.projectId)?.toString();
                return pId === e.target.value;
              });
              setSelectedProjectData(selectedProjectObj || null);
              setErrors((prev) => ({
                ...prev,
                project: undefined,
                job: undefined,
              }));
            }}
            disabled={!selectedWorkspace || isLoadingProjects || isReadOnly}
          >
            <option value="">Select Project</option>
            {isLoadingProjects ? (
              <option value="" disabled>
                Loading projects...
              </option>
            ) : (
              projectOptions.map((project) => {
                const projectId = (
                  project.id || project.projectId
                )?.toString();
                const projectName =
                  project.name || project.projectName || "Unnamed Project";

                return projectId ? (
                  <option key={projectId} value={projectId}>
                    {projectName}
                  </option>
                ) : null;
              })
            )}
          </select>
        </div>

        <div className="form-group">
          <label>
            Job{" "}
            {errors.job && (
              <span className="error-message-inline">{errors.job}</span>
            )}
          </label>
          <select
            value={selectedJob}
            onChange={(e) => {
              setSelectedJob(e.target.value);
              const selectedJobObj = jobOptions.find((j) => {
                const jId = (j.id || j.jobId)?.toString();
                return jId === e.target.value;
              });
              setSelectedJobData(selectedJobObj || null);
              setErrors((prev) => ({ ...prev, job: undefined }));
            }}
            disabled={!selectedProject || isLoadingJobs || isReadOnly}
          >
            <option value="">Select Job</option>
            {isLoadingJobs ? (
              <option value="" disabled>
                Loading jobs...
              </option>
            ) : (
              jobOptions.map((job) => {
                const jobId = (job.id || job.jobId)?.toString();
                const jobName = job.jobName || job.name || "Unnamed Job";

                return jobId ? (
                  <option key={jobId} value={jobId}>
                    {jobName}
                  </option>
                ) : null;
              })
            )}
          </select>
        </div>

        {!isReadOnly && (
          <div className="task-add-tabs">
            <button
              type="button"
              className={`task-add-tab-button ${taskAddMode === "single" ? "active" : ""}`}
              onClick={() => {
                setTaskAddMode("single");
                setBulkPasteContent("");
                setBulkParsedTasks([]);
                setIsBulkReviewMode(false);
                setErrors((prev) => ({ ...prev, job: undefined, title: undefined }));
              }}
              disabled={isSaving}
            >
              Single Add
            </button>
            <button
              type="button"
              className={`task-add-tab-button ${taskAddMode === "bulk" ? "active" : ""}`}
              onClick={() => {
                setTaskAddMode("bulk");
                setBulkPasteContent("");
                setBulkParsedTasks([]);
                setIsBulkReviewMode(false);
                setErrors((prev) => ({ ...prev, job: undefined, title: undefined }));
              }}
              disabled={isSaving}
            >
              Bulk Add
            </button>
          </div>
        )}

        {taskAddMode === "single" ? (
          <>
            <div className="form-group">
              <label>
                Task Title{" "}
                {errors.title && (
                  <span className="error-message-inline">{errors.title}</span>
                )}
              </label>
              <input
                type="text"
                value={taskTitle}
                onChange={(e) => {
                  setTaskTitle(e.target.value);
                  setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder="Enter task title"
                disabled={isReadOnly}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>
                Task Description
              </label>
              <textarea
                value={taskDescription}
                onChange={(e) => {
                  setTaskDescription(e.target.value);
                  setIsDescriptionManuallyEdited(true);
                  if (errors.description) {
                    setErrors(prev => ({ ...prev, description: undefined }));
                  }
                }}
                placeholder="Task description will be auto-populated from title. Type here to customize..."
                disabled={isReadOnly}
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                  backgroundColor: isReadOnly ? "#f5f5f5" : "#fff"
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>
                Bulk Tasks (paste `title&lt;TAB&gt;description`){" "}
                {errors.title && (
                  <span className="error-message-inline">{errors.title}</span>
                )}
              </label>
              <textarea
                value={bulkPasteContent}
                onChange={(e) => {
                  setBulkPasteContent(e.target.value);
                  setIsBulkReviewMode(false);
                  setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                placeholder={"Example:\nTask 1\tDescription 1\nTask 2\tDescription 2"}
                disabled={isReadOnly}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e9ecef",
                  borderRadius: "8px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  minHeight: "120px",
                  resize: "vertical",
                }}
              />
            </div>

            {!isBulkReviewMode ? (
              <div className="bulk-review-actions">
                <button
                  type="button"
                  className="bulk-review-button"
                  onClick={handleBulkReview}
                  disabled={isReadOnly || !bulkPasteContent.trim()}
                >
                  Review
                </button>
                <button
                  type="button"
                  className="bulk-review-button secondary"
                  onClick={handleBulkClear}
                  disabled={
                    isReadOnly ||
                    (!bulkPasteContent.trim() && bulkParsedTasks.length === 0)
                  }
                >
                  Clear
                </button>
              </div>
            ) : (
              <div className="bulk-review-table">
                {bulkParsedTasks.length === 0 ? (
                  <div className="bulk-jobs-empty">No tasks parsed.</div>
                ) : (
                  <>
                    <div className="bulk-table-header">
                      <div>Title</div>
                      <div>Description</div>
                      <div />
                    </div>
                    <div className="bulk-table-body">
                      {bulkParsedTasks.map((t, index) => (
                        <div className="bulk-table-row" key={index}>
                          <input
                            type="text"
                            value={t.title}
                            onChange={(e) =>
                              handleBulkTaskEdit(index, "title", e.target.value)
                            }
                            disabled={isReadOnly}
                            placeholder="Task title"
                          />
                          <textarea
                            value={t.description}
                            onChange={(e) =>
                              handleBulkTaskEdit(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            disabled={isReadOnly}
                            rows={2}
                            placeholder="Task description"
                          />
                          <button
                            type="button"
                            className="bulk-row-delete"
                            onClick={() => handleBulkTaskDelete(index)}
                            disabled={isReadOnly}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="bulk-table-actions">
                      <button
                        type="button"
                        className="bulk-review-button"
                        onClick={handleBulkAddRow}
                        disabled={isReadOnly}
                      >
                        Add Row
                      </button>
                      <button
                        type="button"
                        className="bulk-review-button secondary"
                        onClick={handleBulkClear}
                        disabled={isReadOnly}
                      >
                        Clear
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          {!isReadOnly && (
            <button
              className="save-button"
              onClick={taskAddMode === "bulk" ? handleSaveBulk : handleSave}
              disabled={
                isSaving ||
                (taskAddMode === "bulk"
                  ? !selectedJob ||
                    !bulkParsedTasks.some((t) => (t?.title || "").trim())
                  : !selectedJob)
              }
            >
              {isSaving ? "Saving..." : "Save Task"}
            </button>
          )}
        </div>
        
        {/* Keyboard shortcut hint */}
        <div className="keyboard-hint" style={{ 
          position: 'absolute', 
          bottom: '10px', 
          right: '10px', 
          fontSize: '11px', 
          color: '#999',
          backgroundColor: 'rgba(255,255,255,0.8)',
          padding: '2px 6px',
          borderRadius: '4px',
          pointerEvents: 'none'
        }}>
          Ctrl+S to save
        </div>
      </div>
    </div>
  ) : null;
};

NewTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  refreshNotes: PropTypes.func,
  refreshFilteredNotes: PropTypes.func,
  userworksaces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      text: PropTypes.string,
    }),
  ),
  source: PropTypes.oneOf(["grid", "dashboard"]),
  defaultWorkspaceRole: PropTypes.number,
  fetchNotesWithFilters: PropTypes.func,
  selectedFilters: PropTypes.object,
  hasActiveFilters: PropTypes.bool,
  viewMode: PropTypes.string,
  refreshStackedView: PropTypes.func,
  hasActiveSearchText: PropTypes.bool,
  performSearching: PropTypes.func,
  searchTerm: PropTypes.string,
};

NewTaskModal.defaultProps = {
  refreshNotes: null,
  refreshFilteredNotes: null,
  userworksaces: [],
  source: "dashboard",
  defaultWorkspaceRole: null,
  fetchNotesWithFilters: null,
  selectedFilters: {},
  hasActiveFilters: false,
  viewMode: "cards",
  refreshStackedView: null,
  hasActiveSearchText: false,
  performSearching: null,
  searchTerm: "",
};

export default NewTaskModal;

